import { useEffect, useRef, useState } from "react";
import { Mic, Play, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const VOICE_INFO = {
  alloy: "Neutral, balanced", ash: "Clear, articulate", coral: "Warm, friendly", echo: "Smooth, calm",
  fable: "Expressive, storytelling", nova: "Energetic, upbeat", onyx: "Deep, authoritative", sage: "Wise, measured", shimmer: "Bright, cheerful",
};

export function NarratorSettings() {
  const [settings, setSettings] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState("");
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  useEffect(() => {
    api.get("/admin/settings/tts").then((r) => { setSettings(r.data); setSaved(r.data); }).catch((e) => toast.error(apiError(e)));
    return () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); };
  }, []);

  if (!settings) return <div className="h-48 animate-pulse rounded-2xl bg-white/80" />;

  const dirty = saved && (saved.tts_voice !== settings.tts_voice || saved.tts_speed !== settings.tts_speed);

  const preview = async () => {
    setBusy("preview");
    try {
      const res = await api.post("/admin/settings/tts/preview", { tts_voice: settings.tts_voice, tts_speed: settings.tts_speed }, { responseType: "blob", timeout: 0 });
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = URL.createObjectURL(res.data);
      audioRef.current.src = urlRef.current;
      await audioRef.current.play();
    } catch (e) {
      toast.error(apiError(e, "Preview failed"));
    } finally {
      setBusy("");
    }
  };

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put("/admin/settings/tts", { tts_voice: settings.tts_voice, tts_speed: settings.tts_speed });
      setSaved({ ...settings, ...data });
      toast.success("Narrator updated. Articles will be re-voiced the next time someone presses Listen.");
    } catch (e) {
      toast.error(apiError(e, "Could not save"));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="rounded-2xl border border-[var(--line)] bg-white p-5" data-testid="narrator-settings">
      <div className="flex items-center gap-2">
        <Mic className="h-5 w-5 text-[var(--saffron-deep)]" />
        <p className="eyebrow text-[var(--saffron-deep)]">Narrator voice · "Listen" feature</p>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="block text-sm font-medium text-stone-700">
          Voice
          <select data-testid="narrator-voice-select" value={settings.tts_voice} onChange={(e) => setSettings({ ...settings, tts_voice: e.target.value })} className="mt-1.5 w-full rounded-xl border border-[var(--line)] bg-white px-4 py-2.5 text-base outline-none focus:border-[var(--chakra)]">
            {settings.voices.map((v) => <option key={v} value={v}>{v[0].toUpperCase() + v.slice(1)} · {VOICE_INFO[v]}</option>)}
          </select>
        </label>
        <label className="block text-sm font-medium text-stone-700">
          Reading speed <span className="ml-1 rounded bg-[var(--chakra-tint)] px-1.5 py-0.5 text-xs text-[var(--chakra)]" data-testid="narrator-speed-value">{settings.tts_speed.toFixed(2)}×</span>
          <input data-testid="narrator-speed-range" type="range" min="0.5" max="2" step="0.05" value={settings.tts_speed} onChange={(e) => setSettings({ ...settings, tts_speed: parseFloat(e.target.value) })} className="mt-3 w-full accent-[var(--saffron)]" />
          <div className="flex justify-between text-[11px] text-stone-400"><span>Slower 0.5×</span><span>Normal 1×</span><span>Faster 2×</span></div>
        </label>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button onClick={preview} disabled={!!busy} data-testid="narrator-preview-btn" className="inline-flex items-center gap-2 rounded-full border border-[var(--line)] bg-white px-4 py-2 text-sm font-semibold text-stone-700 transition-colors hover:border-[var(--chakra)] disabled:opacity-60">
          {busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Preview voice
        </button>
        <button onClick={save} disabled={!!busy || !dirty} data-testid="narrator-save-btn" className="inline-flex items-center gap-2 rounded-full bg-[var(--india-green)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--india-green-deep)] disabled:opacity-50">
          {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {dirty ? "Save narrator" : "Saved"}
        </button>
        <audio ref={audioRef} data-testid="narrator-preview-audio" className="hidden" />
        <p className="text-xs text-stone-500">Hindi articles are read by the same voice (it carries a light English accent).</p>
      </div>
    </div>
  );
}
